import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';
import Switch from '@mui/material/Switch';
import FilterListIcon from '@mui/icons-material/FilterList';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';

import { getRoles, deleteRole, toggleRoleStatus } from '../api/roles';
import { usePermissions } from '../context/PermissionsContext';
import {
  Card,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from '../components/ui-kit';
import SearchInput from '../components/SearchInput';
import ConfirmationDialog from '../components/ConfirmationDialog';
import SortableTableHead from '../components/SortableTableHead';
import PermissionFiltersPanel from '../components/PermissionFiltersPanel';
import {
  ROLE_STATUS_OPTIONS,
  ROLE_TYPE_OPTIONS,
} from '../utils/filterConstants';
import { getPermissionChipSx } from '../utils/serviceColors';

// Type (system/custom) is role-specific, so it lives here.
const TypeFilter = React.memo(({ value, onChange }) => (
  <Box>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      Type
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {ROLE_TYPE_OPTIONS.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? 'contained' : 'outlined'}
          size="small"
          onClick={() => onChange(option.value)}
          sx={{ minWidth: 'auto', px: 2 }}
        >
          {option.label}
        </Button>
      ))}
    </Box>
  </Box>
));

// Calculate active filter count
export const getActiveFilterCount = (filters) => [
  filters.nameFilter ? 1 : 0,
  filters.services?.length > 0 ? 1 : 0,
  filters.resources?.length > 0 ? 1 : 0,
  filters.actions?.length > 0 ? 1 : 0,
  filters.statusFilter !== 'all' ? 1 : 0,
  filters.systemFilter !== 'all' ? 1 : 0
].reduce((a, b) => a + b, 0);

function RolesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    return window.innerWidth < 768 ? 'card' : 'table';
  });
  const [nameFilter, setNameFilter] = useState('');
  
  // Search handler
  const handleSearch = useCallback((value) => {
    setNameFilter(value);
  }, []);
  // Permission-based filters (service / resource / action)
  const [permissionFilters, setPermissionFilters] = useState({
    services: [],
    resources: [],
    actions: [],
    status: 'all',
  });
  // Add filter for system roles
  const [systemFilter, setSystemFilter] = useState('all');
  // Add sorting state for all sortable columns
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Function to format permission display
  const formatPermission = (permission) => {
    
    // Check if it's the new structure (permission.permission.service)
    if (permission.permission && permission.permission.service) {
      const { service, resource, action } = permission.permission;
      if (!service || !resource || !action) return 'Incomplete permission';
      return `${service}.${resource}.${action}`;
    }
    
    // Check if it's the direct structure (permission.name contains the full permission)
    if (permission.name && typeof permission.name === 'string') {
      return permission.name;
    }
    
    // Check if it's the old structure (permission.service)
    if (permission.service && permission.resource && permission.action) {
      return `${permission.service}.${permission.resource}.${permission.action}`;
    }
    
    return 'Unknown permission';
  };

  // Function to get permission tooltip text
  const getPermissionTooltip = (permission) => {
    let tooltip = '';
    
    // Check if it's the new structure (permission.permission.service)
    if (permission.permission && permission.permission.service) {
      const { service, resource, action, description } = permission.permission;
      if (!service || !resource || !action) return 'Incomplete permission';
      tooltip = `${service}.${resource}.${action}`;
      

      if (description) {
        tooltip += `\nDescription: ${description}`;
      }
    }
    // Check if it's the direct structure (permission.name contains the full permission)
    else if (permission.name && typeof permission.name === 'string') {
      tooltip = permission.name;
    }
    // Check if it's the old structure (permission.service)
    else if (permission.service && permission.resource && permission.action) {
      tooltip = `${permission.service}.${permission.resource}.${permission.action}`;
    }
    else {
      return 'Invalid permission';
    }
    
    // Add additional info if available
    if (permission.conditions && Object.keys(permission.conditions).length > 0) {
      tooltip += `\nConditions: ${JSON.stringify(permission.conditions)}`;
    }
    
    return tooltip;
  };

  // Returns MUI sx object for permission chip (color + filled/outlined styling)
  const getChipSx = (permission) => {
    const isActive = getPermissionStatus(permission) === 'active';
    return getPermissionChipSx(permission, { isActive });
  };

  // Function to get permission status
  const getPermissionStatus = (permission) => {
    // Check if it's the new structure (permission.permission.isActive)
    if (permission.permission && permission.permission.isActive !== undefined) {
      return permission.permission.isActive ? 'active' : 'inactive';
    }
    // Check if it's the direct structure (permission.isActive)
    if (permission.isActive !== undefined) {
      return permission.isActive ? 'active' : 'inactive';
    }
    // Default to active if no status is specified
    return 'active';
  };

  // Update view mode when window is resized
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setViewMode((current) => {
        if (mobile && current !== 'card') return 'card';
        if (!mobile && current === 'card') return 'table';
        return current;
      });
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset pagination whenever any filter or sort changes
  useEffect(() => {
    setPage(0);
  }, [nameFilter, permissionFilters, systemFilter, sortField, sortDirection]);

  // Handler for switching view modes
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };


  // Handler for SortableTableHead
  const handleSortRequest = useCallback((field) => {
    if (sortField === field) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') {
        setSortField('');
        setSortDirection('asc');
      } else setSortDirection('asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  // Build params for API
  const filterParams = {
    page: page + 1, // API expects 1-based page
    limit: rowsPerPage,
    ...(nameFilter && { name: nameFilter }),
    ...(permissionFilters.services.length > 0 && { service: permissionFilters.services }),
    ...(permissionFilters.resources.length > 0 && { resource: permissionFilters.resources }),
    ...(permissionFilters.actions.length > 0 && { action: permissionFilters.actions }),
    ...(permissionFilters.status !== 'all' && { active: permissionFilters.status === 'active' }),
    ...(systemFilter !== 'all' && { isSystem: systemFilter === 'system' }),
    sort: sortField ? `${sortField}_${sortDirection}` : undefined,
  };

  // Fetch roles list with filters/sorting. staleTime is 0 so that returning
  // to a previously-fetched filter combination (e.g. after clearing a tag or
  // "Clear All Filters") still triggers a fresh request instead of silently
  // serving cached data.
  const { data, isLoading, isError, error } = useQuery(['roles', filterParams], () => getRoles(filterParams), {
    cacheTime: 10 * 60 * 1000,
    keepPreviousData: true,
  });

  // Mutation for deleting a role
  const deleteRoleMutation = useMutation((id) => deleteRole(id), {
    onSuccess: (data) => {
      const message = data?.message || 'Role successfully deleted';
      const usersAffected = data?.usersAffected || 0;
      
      if (usersAffected > 0) {
        toast.success(`${message}. ${usersAffected} user(s) affected.`);
      } else {
        toast.success(message);
      }
      
      queryClient.invalidateQueries(['roles']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error deleting role');
    },
  });

  // Mutation for toggling role status
  const toggleRoleStatusMutation = useMutation((id) => toggleRoleStatus(id), {
    onSuccess: (data) => {
      const status = data.isActive ? 'activated' : 'deactivated';
      toast.success(`Role successfully ${status}`);
      queryClient.invalidateQueries(['roles']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error toggling role status');
    },
  });

  // Handler for navigating to create new role
  const handleCreateRole = () => {
    navigate('/roles/new');
  };

  // Handler for navigating to edit role
  const handleEditRole = (id) => {
    if (!id || id === 'undefined') {
      toast.error('Invalid role ID');
      return;
    }
    navigate(`/roles/${id}`);
  };

  // Open delete role dialog
  const handleOpenDeleteDialog = (role) => {
    if (!role || !role._id) {
      toast.error('Invalid role');
      return;
    }
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  // Close delete role dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedRole(null);
  };

  // Delete role
  const handleDeleteRole = async () => {
    if (!selectedRole) return;

    // Показываем дополнительное предупреждение, если роль привязана к пользователям
    if (selectedRole.userCount > 0) {
      const confirmDelete = window.confirm(
        `Warning: This role is assigned to ${selectedRole.userCount} user(s). ` +
        `The system will automatically remove this role from all affected users and assign the default "user" role if needed. ` +
        `Are you sure you want to proceed?`
      );
      
      if (!confirmDelete) {
        return;
      }
    }

    try {
      await deleteRoleMutation.mutateAsync(selectedRole._id);
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // Toggle role status
  const handleToggleRoleStatus = async (roleId, currentStatus) => {
    try {
      await toggleRoleStatusMutation.mutateAsync(roleId);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  // Calculate active filter count for the main component
  const activeFilterCount = getActiveFilterCount({
    nameFilter,
    services: permissionFilters.services,
    resources: permissionFilters.resources,
    actions: permissionFilters.actions,
    statusFilter: permissionFilters.status,
    systemFilter
  });

  // Handler to clear all filters
  const handleClearAllFilters = () => {
    setNameFilter('');
    setPermissionFilters({ services: [], resources: [], actions: [], status: 'all' });
    setSystemFilter('all');
  };

  const typeFilterNode = useMemo(
    () => (
      <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
        <TypeFilter value={systemFilter} onChange={setSystemFilter} />
      </Box>
    ),
    [systemFilter]
  );

  // Handler for expanding/collapsing table rows
  const handleRowExpand = (roleId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(roleId)) {
      newExpandedRows.delete(roleId);
    } else {
      newExpandedRows.add(roleId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Process roles data to ensure proper structure
  const processedRoles = useMemo(() => {
    if (!data?.roles || !Array.isArray(data.roles)) {
      return [];
    }

    return data.roles.map((role) => {
      // Ensure role has _id
      if (!role._id) {
        return null;
      }

      // Ensure permissions array exists
      if (!role.permissions) {
        role.permissions = [];
      }

      // Filter permissions to ensure valid structure
      if (Array.isArray(role.permissions)) {
        role.permissions = role.permissions.filter(permission => 
          permission && (
            // New structure: permission.permission.service
            (permission.permission && permission.permission.service) ||
            // Direct structure: permission.name (contains full permission string)
            (permission.name && typeof permission.name === 'string') ||
            // Old structure: permission.service
            (permission.service && permission.resource && permission.action)
          )
        );
      }

      return role;
    }).filter(Boolean); // Remove null entries
  }, [data?.roles]);

  // Role Card Component
  const RoleCard = ({ role }) => {
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = () => {
      setExpanded(!expanded);
    };

    return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '12px',
        background: (theme) =>
          `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
        boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
        color: 'text.primary',
        overflow: 'hidden',
        position: 'relative',
        p: { xs: 1.5, sm: 2.5 },
        '&:hover': {
          boxShadow: (theme) => `0 12px 24px ${theme.palette.common.black}30`,
        },
      }}
    >
      {/* Header - Name and Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.2,
            fontSize: { xs: '1rem', sm: '1.1rem' },
          }}
        >
          {role.name || 'Unnamed'}
        </Typography>
            {role.priority > 0 && (
              <Chip
                label={`Priority: ${role.priority}`}
                size="small"
                color="info"
                sx={{ fontSize: '0.6rem', height: '18px' }}
              />
            )}
          </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            aria-label="edit"
            onClick={() => handleEditRole(role._id)}
            disabled={
              !hasPermission('auth-service', 'role', 'update') || 
              !role._id || 
              role.isSystem
            }
            sx={{
              width: 28,
              height: 28,
              padding: 0,
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'action.hover',
              },
              transition: 'all 0.2s',
              opacity: role.isSystem ? 0.5 : 1,
            }}
            title={role.isSystem ? 'System roles cannot be edited' : 'Edit role'}
          >
            <EditIcon fontSize="small" />
          </IconButton>

          <IconButton
            aria-label="delete"
            onClick={() => handleOpenDeleteDialog(role)}
            disabled={
              !hasPermission('auth-service', 'role', 'delete') ||
              role.isSystem
            }
            sx={{
              width: 28,
              height: 28,
              padding: 0,
              color: 'error.main',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.error.light + '20',
              },
              transition: 'all 0.2s',
              opacity:
                !hasPermission('auth-service', 'role', 'delete') ||
                role.isSystem ||
                !role._id
                  ? 0.5
                  : 1,
            }}
            title={role.isSystem ? 'System roles cannot be deleted' : 'Delete role'}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Description */}
      {role.description && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.85rem',
            }}
          >
            {role.description}
          </Typography>
        </Box>
      )}

        {/* Role Status */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label={role.isActive ? 'Active' : 'Inactive'}
            color={role.isActive ? 'success' : 'error'}
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
          <Switch
            checked={role.isActive || false}
            onChange={() => handleToggleRoleStatus(role._id, role.isActive)}
            disabled={
              !hasPermission('auth-service', 'role', 'update') ||
              role.isSystem ||
              toggleRoleStatusMutation.isLoading
            }
            color="success"
            size="small"
          />
          {role.isSystem && (
            <Chip
              label="System Role"
              color="warning"
              size="small"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
          {role.metadata?.createdAt && (
            <Chip
              label={`Created: ${new Date(role.metadata.createdAt).toLocaleDateString()}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
          <Chip
            label={`${role.userCount || 0} Users`}
            color="info"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        </Box>

      {/* Permissions */}
      <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }} onClick={handleExpandClick}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', fontWeight: 600 }}>
              Permissions ({Array.isArray(role.permissions) ? role.permissions.length : 0}):
        </Typography>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {Array.isArray(role.permissions) && role.permissions.length > 0 ? (
            role.permissions.map((permission, index) => (
              <Chip
                key={index}
                label={formatPermission(permission)}
                size="small"
                title={getPermissionTooltip(permission)}
                sx={{
                  height: '20px',
                  fontSize: '0.7rem',
                  '& .MuiChip-label': { px: 1 },
                  opacity: getPermissionStatus(permission) === 'inactive' ? 0.6 : 1,
                  ...getChipSx(permission),
                }}
              />
            ))
              ) : role.permissions === undefined ? (
                <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  Loading permissions...
                </Typography>
          ) : (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  No permissions assigned
                </Typography>
              )}
            </Box>
          </Collapse>
          
          {/* Show preview when collapsed */}
          {!expanded && Array.isArray(role.permissions) && role.permissions.length > 0 && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {/* Show first 3 permissions as preview */}
                {role.permissions.slice(0, 3).map((permission, index) => (
                  <Chip
                    key={index}
                    label={formatPermission(permission)}
                    size="small"
                    title={getPermissionTooltip(permission)}
                    sx={{
                      height: '20px',
                      fontSize: '0.7rem',
                      '& .MuiChip-label': { px: 1 },
                      opacity: getPermissionStatus(permission) === 'inactive' ? 0.6 : 1,
                      ...getChipSx(permission),
                    }}
                  />
                ))}
                {/* Show count if more than 3 */}
                {role.permissions.length > 3 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center', ml: 0.5 }}>
                    +{role.permissions.length - 3} more
            </Typography>
          )}
        </Box>
              {/* Show valid/invalid count when collapsed */}
              <Typography sx={{ color: 'text.secondary', fontSize: '0.7rem', mb: 1, fontStyle: 'italic' }}>
                {role.permissions.filter(p => p && (
                  (p.permission && p.permission.service) ||
                  (p.name && typeof p.name === 'string') ||
                  (p.service && p.resource && p.action)
                )).length} valid, {role.permissions.length - role.permissions.filter(p => p && (
                  (p.permission && p.permission.service) ||
                  (p.name && typeof p.name === 'string') ||
                  (p.service && p.resource && p.action)
                )).length} invalid
              </Typography>
            </>
          )}
      </Box>
    </Card>
  );
  };

  // Check that data exists and is an array
  const rolesArray = processedRoles;

  // Define columns for SortableTableHead
  const ROLE_TABLE_COLUMNS = [
    { field: 'name', headerName: 'Name', sortable: true, minWidth: 150, width: '12%' },
    { field: 'description', headerName: 'Description', sortable: true, minWidth: 150, width: '15%' },
    { field: 'permissions', headerName: 'Permissions', sortable: false, minWidth: 200, width: '18%' },
    { field: 'userCount', headerName: 'Users', sortable: false, minWidth: 80, width: '8%' },
    { field: 'isActive', headerName: 'Active', sortable: true, minWidth: 100, width: '9%' },
    { field: 'isSystem', headerName: 'System', sortable: true, minWidth: 100, width: '10%' },
    { field: 'actions', headerName: 'Actions', sortable: false, align: 'left', minWidth: 120, width: '10%' },
  ];

  return (
    <Box sx={{ pb: 3 }}>
      {/* Header: Title, Toggle, Create Button */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          mb: 3,
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="500">
          Role Management
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', sm: 'flex-end' },
            gap: 2,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {hasPermission('auth-service', 'role', 'create') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateRole}
            >
              Create Role
            </Button>
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
            sx={{ justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
          >
            <ToggleButton value="table" aria-label="table view">
              <TableRowsIcon />
            </ToggleButton>
            <ToggleButton value="card" aria-label="card view">
              <GridViewIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Search and filter section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <SearchInput
              placeholder="Search roles by name..."
              onSearch={handleSearch}
              defaultValue={nameFilter}
              fullWidth
              size="small"
              sx={{ flexGrow: 1 }}
            />
          </Box>
          {/* Filter icon positioned to the right of search */}
          <Tooltip title="Filter roles">
            <IconButton
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              color={activeFilterCount > 0 ? "primary" : "default"}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                boxShadow: activeFilterCount > 0 ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
                '&:hover': {
                  backgroundColor: activeFilterCount > 0
                    ? 'rgba(25, 118, 210, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)',
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Badge badgeContent={activeFilterCount} color="primary">
                <FilterListIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Filter panel that expands below */}
        <PermissionFiltersPanel
          title="Filter Roles"
          value={permissionFilters}
          onChange={setPermissionFilters}
          statusOptions={ROLE_STATUS_OPTIONS}
          extraFilters={typeFilterNode}
          isExpanded={isFiltersExpanded}
          onToggleExpand={() => setIsFiltersExpanded(!isFiltersExpanded)}
          onClearAll={handleClearAllFilters}
        />
      </Box>

      {/* Table/Card Content with loading state */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ my: 2 }}>
          Error loading roles: {error?.message || 'Unknown error'}
        </Typography>
      ) : (
        <>
          {viewMode === 'table' ? (
            <>
              <TableContainer
                sx={{
                  overflowX: 'auto', // Ensures horizontal scrolling
                  boxShadow: (theme) => theme.shadows[1],
                  borderRadius: 1,
                  mb: 2
                }}
              >
                <Table
                  sx={{
                    minWidth: 900,
                    tableLayout: 'fixed', // Ensures columns respect their width settings
                    width: '100%'
                  }}
                  aria-label="roles table"
                >
                  <SortableTableHead
                    columns={ROLE_TABLE_COLUMNS}
                    sortConfig={{ field: sortField, direction: sortDirection }}
                    handleSortRequest={handleSortRequest}
                  />
              <TableBody>
                {rolesArray.map((role, index) => (
                  <TableRow
                    key={role._id || `role-${index}`}
                    hover
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      backgroundColor: role.isActive
                        ? 'inherit'
                        : 'action.disabledBackground',
                    }}
                  >
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{role.name || 'Unnamed'}</TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{role.description || ''}</TableCell>
                    <TableCell sx={{ overflow: 'hidden' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Permissions Preview */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            {Array.isArray(role.permissions) && role.permissions.length > 0 ? (
                            <>
                              {/* Show first 3 permissions */}
                              {role.permissions.slice(0, 3).map((permission, idx) => (
                            <Chip
                                  key={idx}
                                  label={formatPermission(permission)}
                                  size="small"
                                  title={getPermissionTooltip(permission)}
                                  sx={{
                                    mr: 0.5,
                                    mb: 0.5,
                                    opacity: getPermissionStatus(permission) === 'inactive' ? 0.6 : 1,
                                    ...getChipSx(permission),
                                  }}
                                />
                              ))}
                              {/* Show expand button if there are more permissions */}
                              {role.permissions.length > 3 && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleRowExpand(role._id)}
                                  sx={{ ml: 0.5 }}
                                >
                                  {expandedRows.has(role._id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              )}
                              {/* Show count if more than 3 */}
                              {role.permissions.length > 3 && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                                  +{role.permissions.length - 3} more
                                </Typography>
                              )}
                            </>
                          ) : role.permissions === undefined ? (
                            <span>Loading permissions...</span>
                          ) : (
                            <span>No permissions assigned</span>
                          )}
                        </Box>
                        
                        {/* Expanded Permissions */}
                        {Array.isArray(role.permissions) && role.permissions.length > 3 && (
                          <Collapse in={expandedRows.has(role._id)} timeout="auto" unmountOnExit>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                              {role.permissions.slice(3).map((permission, idx) => (
                                <Chip
                                  key={idx + 3}
                                  label={formatPermission(permission)}
                                  size="small"
                                  title={getPermissionTooltip(permission)}
                                  sx={{
                                    mr: 0.5,
                                    mb: 0.5,
                                    opacity: getPermissionStatus(permission) === 'inactive' ? 0.6 : 1,
                                    ...getChipSx(permission),
                                  }}
                                />
                              ))}
                            </Box>
                          </Collapse>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px 16px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Chip
                          label={role.userCount || 0}
                          color="info"
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            minWidth: '40px'
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px 16px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Switch
                          checked={role.isActive || false}
                          onChange={() => handleToggleRoleStatus(role._id, role.isActive)}
                          disabled={
                            !hasPermission('auth-service', 'role', 'update') ||
                            role.isSystem ||
                            toggleRoleStatusMutation.isLoading
                          }
                          color="success"
                          size="small"
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            ml: 1,
                            color: role.isActive ? 'success.main' : 'error.main',
                            fontWeight: 500
                          }}
                        >
                          {role.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px 16px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {role.isSystem ? (
                          <Chip
                            label="System"
                            color="warning"
                            size="small"
                            variant="filled"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              minWidth: '60px'
                            }}
                          />
                        ) : (
                          <Chip
                            label="Custom"
                            color="default"
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              minWidth: '60px'
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="left" sx={{ width: '120px', padding: '8px 16px' }}>
                      {/* Fixed width action buttons container */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          minWidth: 80, // Ensures minimum width for the actions
                          whiteSpace: 'nowrap' // Prevents wrapping
                        }}
                      >
                        <IconButton
                          aria-label="edit"
                          onClick={() => handleEditRole(role._id)}
                          disabled={
                            !hasPermission('auth-service', 'role', 'update') || 
                            !role._id || 
                            role.isSystem
                          }
                          sx={{ 
                            '&:hover': { color: 'primary.main' },
                            opacity: role.isSystem ? 0.5 : 1
                          }}
                          title={role.isSystem ? 'System roles cannot be edited' : 'Edit role'}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          onClick={() => handleOpenDeleteDialog(role)}
                          disabled={
                            !hasPermission('auth-service', 'role', 'delete') ||
                            role.isSystem
                          }
                          color="error"
                          sx={{
                            '&:hover': {
                              backgroundColor: (theme) =>
                                theme.palette.error.light + '20',
                            },
                            opacity: role.isSystem ? 0.5 : 1
                          }}
                          title={role.isSystem ? 'System roles cannot be deleted' : 'Delete role'}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                    {rolesArray.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                            <Typography variant="body2" color="textSecondary">
                              No roles found
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={data?.pagination?.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
            </>
          ) : (
            <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(auto-fill, minmax(280px, 1fr))',
                md: 'repeat(auto-fill, minmax(350px, 1fr))',
              },
              gap: { xs: 2, sm: 3 },
              width: '100%',
                  mb: 2,
                  mt: 2
            }}
          >
            {rolesArray.map((role, index) => (
              <RoleCard key={role._id || `role-${index}`} role={role} />
            ))}
                {rolesArray.length === 0 && (
                  <Box sx={{ gridColumn: '1 / -1', py: 4, textAlign: 'center' }}>
                    <Typography variant="body1">
                      No roles found
                    </Typography>
          </Box>
                )}
        </Box>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data?.pagination?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
            </>
          )}
        </>
      )}

      {/* Delete role confirmation dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteRole}
        title="Delete Role"
        contentText={
          selectedRole ? (
            <>
              You are about to delete role "{selectedRole.name}".
              {selectedRole.userCount > 0 ? (
                <>
                  {' '}This role is currently assigned to {selectedRole.userCount} user(s).
                  {' '}The system will automatically remove this role from all affected users.
                  {selectedRole.userCount === 1 ? ' If the user has no other roles, they will be assigned the default "user" role.' : ' If any users have no other roles, they will be assigned the default "user" role.'}
                </>
              ) : (
                ' This role is not assigned to any users.'
              )}
              {' '}This action cannot be undone.
            </>
          ) : (
            'You are about to delete a role. This action cannot be undone.'
          )
        }
        confirmButtonText={
          deleteRoleMutation.isLoading ? 'Deleting...' : 'Delete Role'
        }
        confirmButtonColor="error"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default RolesList;
